import React, { useContext, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleArrowDown,
  faCircleArrowLeft,
  faCircleArrowRight,
  faCircleArrowUp,
} from '@fortawesome/pro-solid-svg-icons';
import { get } from 'lodash';
import { SocketContext } from '../../socketProvider';
import { socketEventNames } from './helper';
import store from '../../store';

import style from './style.module.scss';

const { ROVER_MOVE, ROVER_GET_POSITION } = socketEventNames;

const continuousMoveHelper = (cmd, key, speedFactor = 1) => {
  if (!['move', 'stop'].includes(cmd) || !['w', 'a', 's', 'd', 'e', 'q'].includes(key)) {
    return null;
  }

  const { mapping } = store.getState();
  const connectedCamera = get(mapping, 'connectedCamera');

  if (cmd === 'move') {
    const panSpeed = get(mapping, 'preview.continuousSpeed.pan.value');
    const tiltSpeed = get(mapping, 'preview.continuousSpeed.tilt.value');
    const panSpeedMax = get(mapping, 'preview.continuousSpeed.pan.max');
    const tiltSpeedMax = get(mapping, 'preview.continuousSpeed.tilt.max');
    const zoomSpeed = get(mapping, 'preview.continuousSpeed.zoom.value');
    const zoomSpeedMax = get(mapping, 'preview.continuousSpeed.zoom.max');

    let ROVER_MOVE_CMD = '';

    switch (key) {
    case 'w':
      ROVER_MOVE_CMD = `c0,${Math.min((Number(tiltSpeed) / Number(tiltSpeedMax)) * speedFactor, 1.0)}`;
      return { connectedCamera, ROVER_MOVE_CMD };
    case 'a':
      ROVER_MOVE_CMD = `c${Math.max((Number(-1 * panSpeed) / Number(panSpeedMax)) * speedFactor, -1.0)},0`;
      return { connectedCamera, ROVER_MOVE_CMD };
    case 's':
      ROVER_MOVE_CMD = `c0,${Math.max((Number(-1 * tiltSpeed) / Number(tiltSpeedMax)) * speedFactor, -1.0)}`;
      return { connectedCamera, ROVER_MOVE_CMD };
    case 'd':
      ROVER_MOVE_CMD = `c${Math.min((Number(panSpeed) / Number(panSpeedMax)) * speedFactor, 1.0)},0`;
      return { connectedCamera, ROVER_MOVE_CMD };
    case 'q':
      ROVER_MOVE_CMD = `c0,0,${Math.max(-(Number(zoomSpeed) / Number(zoomSpeedMax)) * speedFactor, -1.0)}`;
      return { connectedCamera, ROVER_MOVE_CMD };
    case 'e':
      ROVER_MOVE_CMD = `c0,0,${Math.min((Number(zoomSpeed) / Number(zoomSpeedMax)) * speedFactor, 1.0)}`;
      return { connectedCamera, ROVER_MOVE_CMD };
    default:
      return null;
    }
  } else if (cmd === 'stop') {
    const ROVER_MOVE_CMD = 's';
    const updatePositionOnMove = get(mapping, 'preview.continuousSpeed.updatePosition');
    return { connectedCamera, ROVER_MOVE_CMD, updatePositionOnMove };
  }

  return null;
};

const KeyboardControls = ({ socket, connectedCamera }) => {
  const keyStates = useRef({
    w: false,
    a: false,
    s: false,
    d: false,
    e: false,
    q: false,
  });

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (/^(input|select|textarea)$/.exec(document.activeElement.tagName.toLowerCase()) || keyStates.current[event.key.toLowerCase()]) {
        return;
      }

      let cmd = '';
      // Multiply speed by 1.5 if the key is uppercase (WASDQE)
      const speedFactor = /^[WASDQE]$/.test(event.key) ? 1.5 : 1;

      switch (event.key.toLowerCase()) {
      case 'w':
        console.log('Sending socket message for moving up');
        cmd = continuousMoveHelper('move', 'w', speedFactor);
        socket.emit(ROVER_MOVE, 'host', cmd.connectedCamera, cmd.ROVER_MOVE_CMD);
        keyStates.current = { ...keyStates.current, w: true };
        break;
      case 'a':
        console.log('Sending socket message for moving left');
        cmd = continuousMoveHelper('move', 'a', speedFactor);
        socket.emit(ROVER_MOVE, 'host', cmd.connectedCamera, cmd.ROVER_MOVE_CMD);
        keyStates.current = { ...keyStates.current, a: true };
        break;
      case 's':
        console.log('Sending socket message for moving down');
        cmd = continuousMoveHelper('move', 's', speedFactor);
        socket.emit(ROVER_MOVE, 'host', cmd.connectedCamera, cmd.ROVER_MOVE_CMD);
        keyStates.current = { ...keyStates.current, s: true };
        break;
      case 'd':
        console.log('Sending socket message for moving right');
        cmd = continuousMoveHelper('move', 'd', speedFactor);
        socket.emit(ROVER_MOVE, 'host', cmd.connectedCamera, cmd.ROVER_MOVE_CMD);
        keyStates.current = { ...keyStates.current, d: true };
        break;
      case 'e':
        console.log('Sending socket message for zoom in');
        cmd = continuousMoveHelper('move', 'e', speedFactor);
        socket.emit(ROVER_MOVE, 'host', cmd.connectedCamera, cmd.ROVER_MOVE_CMD);
        keyStates.current = { ...keyStates.current, e: true };
        break;
      case 'q':
        console.log('Sending socket message for zoom out');
        cmd = continuousMoveHelper('move', 'q', speedFactor);
        socket.emit(ROVER_MOVE, 'host', cmd.connectedCamera, cmd.ROVER_MOVE_CMD);
        keyStates.current = { ...keyStates.current, q: true };
        break;
      default:
        break;
      }

      console.info({ cmd, speedFactor, event });
    };

    const handleKeyUp = (event) => {
      const key = event.key.toLowerCase();
      const cmd = continuousMoveHelper('stop', key);

      if (cmd) {
        socket.emit(ROVER_MOVE, 'host', cmd.connectedCamera, cmd.ROVER_MOVE_CMD);
        keyStates.current = { ...keyStates.current, [key]: false };

        if (cmd.updatePositionOnMove) {
          setTimeout(() => socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#'), 750);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [connectedCamera]);

  return null;
};

const MoveControls = () => {
  const socket = useContext(SocketContext);
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const panSpeed = useSelector(({ mapping }) => get(mapping, 'preview.stepSpeed.pan.value'));
  const tiltSpeed = useSelector(({ mapping }) => get(mapping, 'preview.stepSpeed.tilt.value'));

  return (
    <div className={style.arrowControls}>
      <KeyboardControls socket={socket} connectedCamera={connectedCamera} />
      <div>
        <button
          type="button"
          onClick={() => {
            console.warn('relative move up');
            // e.g {,2,10000,false --> relative move tilt by 10000
            socket.emit(ROVER_MOVE, 'host', connectedCamera, `{,2,${-1 * tiltSpeed}`);
          }}
        >
          <FontAwesomeIcon icon={faCircleArrowUp} />
        </button>
      </div>
      <div>
        <button
          type="button"
          onClick={() => {
            console.warn('relative move left');
            // e.g [,1,10000,false --> relative move pan by 10000
            socket.emit(ROVER_MOVE, 'host', connectedCamera, `[,1,${-1 * panSpeed}`);
          }}
        >
          <FontAwesomeIcon icon={faCircleArrowLeft} />
        </button>
        <button
          type="button"
          onClick={() => {
            console.warn('relative move down');
            // e.g [,1,10000,false --> relative move pan by 10000
            socket.emit(ROVER_MOVE, 'host', connectedCamera, `},2,${tiltSpeed}`);
          }}
        >
          <FontAwesomeIcon icon={faCircleArrowDown} />
        </button>
        <button
          type="button"
          onClick={() => {
            console.warn('relative move right');
            // e.g [,1,10000,false --> relative move pan by 10000
            socket.emit(ROVER_MOVE, 'host', connectedCamera, `],1,${panSpeed}`);
          }}
        >
          <FontAwesomeIcon icon={faCircleArrowRight} />
        </button>
      </div>
    </div>
  );
};

export default MoveControls;
